import React, { forwardRef } from 'react';

import { styled } from '@spazious/dollhouse-theme';

import { Backdrop } from '../Backdrop/index.js';
import { Modal, ModalProps } from '../Modal/index.js';
import { Transition } from '../Transition/index.js';

import { DialogContainer } from './DialogContainer.js';
import { DialogContent } from './DialogContent.js';
import { DialogPaper, DialogPaperProps } from './DialogPaper.js';
import { DialogTitle } from './DialogTitle.js';

const DialogRoot = styled.div`
  position: fixed;
  inset: 0px;
  z-index: 1300;
`;

export interface DialogProps
  extends Omit<ModalProps<typeof DialogRoot>, 'children'>,
    Pick<DialogPaperProps, 'maxWidth' | 'minHeight' | 'height' | 'width'> {
  title?: string;
  message?: string;
  scroll?: 'body' | 'paper';
  children?: React.ReactNode;
  paperProps?: Omit<DialogPaperProps, 'scroll'>;
  fullHeight?: boolean;
  fullWidth?: boolean;
}

export const Dialog = forwardRef(
  (
    {
      title,
      message,
      children,
      scroll = 'paper',
      open,
      onClose,
      maxWidth,
      minHeight,
      fullWidth,
      width = fullWidth ? '100%' : undefined,
      fullHeight,
      height = fullHeight ? '100%' : undefined,
      paperProps,
      backdropComponent = Backdrop,
      component = DialogRoot,
      ...restProps
    }: DialogProps,
    ref: unknown
  ) => {
    return (
      <Modal
        ref={ref}
        backdropComponent={backdropComponent}
        component={component}
        open={open}
        onClose={onClose}
        {...(restProps as any)}
      >
        <DialogContainer
          as={Transition.Fade}
          in={open}
          scroll={scroll}
          onClick={(evt: any) => {
            if (evt.target !== evt.currentTarget) return;
            onClose?.(evt, 'backdropClick');
          }}
        >
          <DialogPaper
            height={height}
            maxWidth={maxWidth}
            minHeight={minHeight}
            scroll={scroll}
            width={width}
            {...paperProps}
            // CUSTOM PROPS
            backgroundColor="var(--spz-sys-color-surface)"
          >
            {title && <DialogTitle>{title}</DialogTitle>}
            {message && <DialogContent>{message}</DialogContent>}
            {children}
          </DialogPaper>
        </DialogContainer>
      </Modal>
    );
  }
);
